import { decide, type AiDecision, type AiOptions } from '../ai';
import type { Action, MatchView } from '../engine';

export interface AiClient {
  decide(view: MatchView, legal: readonly Action[], options: AiOptions): Promise<AiDecision>;
  dispose(): void;
}

/** Runs the AI on the main thread, yielding first so the UI can paint. */
export function createInlineAiClient(): AiClient {
  return {
    decide: (view, legal, options) =>
      new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            resolve(decide(view, legal, options));
          } catch (e) {
            reject(e instanceof Error ? e : new Error(String(e)));
          }
        }, 0);
      }),
    dispose: () => undefined,
  };
}

interface Pending {
  readonly resolve: (d: AiDecision) => void;
  readonly reject: (e: Error) => void;
  readonly view: MatchView;
  readonly legal: readonly Action[];
  readonly options: AiOptions;
}

/**
 * Runs the AI in a Web Worker, falling back to the main thread if workers are unavailable or fail.
 * The worker starts on the first decision and again after dispose(), so a client can outlive a
 * dispose/reuse cycle (React StrictMode runs effect cleanups once in development).
 */
export function createAiClient(): AiClient {
  const inline = createInlineAiClient();
  if (typeof Worker === 'undefined') return inline;
  const pending = new Map<number, Pending>();
  let worker: Worker | null = null;
  let nextId = 1;
  let broken = false;

  const failOver = () => {
    broken = true;
    worker?.terminate();
    worker = null;
    for (const [id, p] of pending) {
      pending.delete(id);
      inline.decide(p.view, p.legal, p.options).then(p.resolve, p.reject);
    }
  };

  const ensureWorker = (): Worker | null => {
    if (worker || broken) return worker;
    try {
      const w = new Worker(new URL('./ai.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (
        e: MessageEvent<{ id: number; ok: boolean; decision?: AiDecision; error?: string }>,
      ) => {
        const p = pending.get(e.data.id);
        if (!p) return;
        pending.delete(e.data.id);
        if (e.data.ok && e.data.decision) p.resolve(e.data.decision);
        else p.reject(new Error(e.data.error ?? 'AI failed'));
      };
      w.onerror = (e) => {
        e.preventDefault();
        failOver();
      };
      worker = w;
    } catch {
      broken = true;
    }
    return worker;
  };

  return {
    decide(view, legal, options) {
      const w = ensureWorker();
      if (!w) return inline.decide(view, legal, options);
      return new Promise((resolve, reject) => {
        const id = nextId++;
        pending.set(id, { resolve, reject, view, legal, options });
        try {
          w.postMessage({ id, view, legal, options });
        } catch {
          failOver();
        }
      });
    },
    dispose() {
      worker?.terminate();
      worker = null;
      // Decisions still pending belong to matches that are being closed.
      pending.clear();
    },
  };
}
